import { createContext, useContext } from "react";
import { fetchSupportedLocales } from "./fetchSupportedLocales";
import { getRegion } from "../services/api/regionApi";
import { getEnvironment } from "../services/api/environmentApi";
import difference from "lodash/difference";
import { GetAllLocaleSettings } from "../queries/GetAllLocaleSettings";
import { contentfulClient } from "./contentfulClient";
import intersection from "lodash/intersection";
import { addLocaleFontScalarsToDocument } from "./addLocaleFontScalarsToDocument";

type LocaleSettings = {
  id: string;
  active: boolean;
  locked: boolean;
  lineBreaksAllowed: boolean;
  localName: string;
  mobileFontScaling: number;
  desktopFontScaling: number;
};

// All data required for the app to initialize
export type GlobalContextType = {
  locale: string;
  localeSettings: LocaleSettings;
  region: string;
  isRegionLocked: boolean;
  supportedLocales: Array<string>;
  lockedLocales: Array<string>;
  unlockedLocales: Array<string>;
  actualRegion: string | null;
  previewContent: boolean;
  switchableLocaleSettings: Array<LocaleSettings>;
  environment: string;
  bypassRegionForwarding: boolean;
  bypassLocaleResolveTo: boolean;
  bypassLocaleLock: boolean;
  bypassLocaleDisable: boolean;
};

export const GlobalContext = createContext<null | GlobalContextType>(null);

export const useGlobalContext = () => {
  const globalContext = useContext(GlobalContext);
  if (!globalContext) {
    throw new Error("GlobalContext data has not been provided.");
  }

  return globalContext;
};

const deconstructLocale = (
  locale: string,
  supportedLocales: Array<string>
): { lang: string; region: string; id: string } | null => {
  if (!locale) {
    return null;
  }

  const [lang, region] = locale.split("-");

  if (!lang || !region) {
    return null;
  }

  if (supportedLocales.includes(locale)) {
    return { lang, region, id: locale };
  } else return null;
};

const getLocaleFromPath = (supportedLocales: Array<string>): string | null => {
  const [, localePathSegment] = location.pathname.split("/");
  if (!localePathSegment) {
    return null;
  }

  const [lang, region] = localePathSegment.split("-");

  if (!lang || !region) {
    return null;
  }

  const locale = `${lang}-${region.toLocaleUpperCase()}`;

  if (supportedLocales.includes(locale)) {
    return locale;
  } else return null;
};

export const fetchInitialGlobalContext =
  async (): Promise<GlobalContextType> => {
    const [
      supportedLocalesSettledResult,
      localeSettingsSettledResult,
      regionSettledResult,
      environmentSettledResult,
    ] = await Promise.allSettled([
      fetchSupportedLocales(),
      contentfulClient.query(GetAllLocaleSettings, {}),
      getRegion(),
      getEnvironment(),
    ]);

    const actualRegion =
      regionSettledResult.status === "fulfilled"
        ? regionSettledResult.value.response?.region
        : null;

    if (
      supportedLocalesSettledResult.status === "rejected" ||
      regionSettledResult.status === "rejected" ||
      environmentSettledResult.status === "rejected" ||
      localeSettingsSettledResult.status === "rejected"
    ) {
      throw new Error("Required data for app startup failed.");
    }

    const environment = environmentSettledResult.value.environment ?? "prod",
      bypassRegionForwarding =
        environmentSettledResult.value.bypassRegionForwarding ?? false,
      bypassLocaleResolveTo =
        environmentSettledResult.value.bypassLocaleResolveTo ?? false,
      bypassLocaleLock =
        environmentSettledResult.value.bypassLocaleLock ?? false,
      bypassLocaleDisable =
        environmentSettledResult.value.bypassLocaleDisable ?? false,
      supportedLocalesResult = supportedLocalesSettledResult.value,
      localeSettingsResult =
        localeSettingsSettledResult.value.data?.localeCollection?.items.filter(
          Boolean
        ) ?? [],
      supportedLocales = intersection(
        supportedLocalesSettledResult.value.locales,
        localeSettingsResult
          .filter((locale) => {
            if (!locale) return false;
            if (bypassLocaleDisable) return true;

            return locale.active;
          })
          .map((locale) => locale?.id)
          .filter(Boolean)
      ),
      region = regionSettledResult.value?.response?.region ?? "US",
      locale = (() => {
        const pathLocale = getLocaleFromPath(supportedLocales);

        const regionAvailableDeconstructedLocales = (supportedLocales ?? [])
          .map((l) => deconstructLocale(l, supportedLocales))
          .filter((l) => bypassRegionForwarding || (l && l.region === region));

        // e.g. ["en-US", "en"] would map to ["en", "en"]
        const navigatorLangs = navigator.languages.map(
          (lang) => lang.split("-")[0]
        );

        const navigatorLangForRegion = navigatorLangs.find(
          (lang) =>
            !!regionAvailableDeconstructedLocales.find(
              (loc) => loc?.id === `${lang}-${region.toLocaleUpperCase()}`
            )
        );

        const navigatorLocaleForRegion = navigatorLangForRegion
          ? `${navigatorLangForRegion}-${region.toLocaleUpperCase()}`
          : null;

        const pathLocaleIsValidlyInRegion =
          regionAvailableDeconstructedLocales.some((l) => l?.id === pathLocale);

        const defaultLocaleForRegion = regionAvailableDeconstructedLocales.find(
          (l) =>
            localeSettingsResult.find((ls) => ls.id === l)?.defaultForRegion
        )?.id;

        const firstLocaleForRegion = regionAvailableDeconstructedLocales[0]?.id;

        // Try to use the locale already in the path
        const locale =
          pathLocaleIsValidlyInRegion && pathLocale
            ? pathLocale
            : // Then fallback to a locale determined by combining the navigator lang + region
            navigatorLangForRegion
            ? navigatorLocaleForRegion!
            : // Then fallback to the region default
            defaultLocaleForRegion
            ? defaultLocaleForRegion!
            : // Finally just use the first available for the region if nothing else
              firstLocaleForRegion!;

        const localeSettings = localeSettingsResult.find(
          ({ id }) => locale === id
        );

        if (!localeSettings) return supportedLocalesResult.default;
        else if (localeSettings?.resolveTo?.id && !bypassLocaleResolveTo) {
          return localeSettings?.resolveTo?.id;
        } else return locale;
      })(),
      activeRegion =
        (locale ? deconstructLocale(locale, supportedLocales)?.region : null) ??
        region,
      allLocaleSettings = localeSettingsResult
        .filter((l) => l.id && supportedLocales.includes(l.id))
        .map((l) => ({
          id: l.id!,
          active: l.active ?? false,
          locked: l?.locked ?? false,
          lineBreaksAllowed: l?.lineBreaksAllowed ?? true,
          localName: l?.localName ?? l?.name ?? "",
          mobileFontScaling: l.mobileFontScaling ?? 100,
          desktopFontScaling: l.desktopFontScaling ?? 100,
          lineHeightScaling: l.lineHeightScaling ?? 100,
        })),
      switchableLocaleSettings = allLocaleSettings.filter(
        (l) =>
          activeRegion === deconstructLocale(l.id, supportedLocales)?.region
      ),
      localeSettings = allLocaleSettings.find((l) => l.id === locale)!,
      isRegionLocked = !bypassLocaleLock && !!localeSettings?.locked,
      unlockedLocales = supportedLocales.filter((locale) => {
        const settings = localeSettingsResult.find(({ id }) => id === locale);
        if (!settings) return false;
        return !settings.locked;
      }),
      lockedLocales = difference(supportedLocales, unlockedLocales),
      previewContent = environmentSettledResult?.value.preview ?? false;

    addLocaleFontScalarsToDocument(localeSettings);

    return {
      locale,
      localeSettings,
      region,
      isRegionLocked,
      supportedLocales,
      actualRegion,
      previewContent,
      unlockedLocales,
      lockedLocales,
      switchableLocaleSettings,
      environment,
      bypassRegionForwarding,
      bypassLocaleResolveTo,
      bypassLocaleLock,
      bypassLocaleDisable,
    };
  };
